/**
 * Create or rename a department or a team.
 *
 * One dialog for both, because the fields are nearly the same and the
 * difference is a single parent field: a department may sit inside another
 * department; a team must sit inside one.
 *
 * The cycle check ("a department cannot be inside its own child") lives on the
 * server, which walks the tree. This form does not attempt it — a partial copy
 * of a graph rule is a rule that disagrees with itself.
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiError, Alert, Button, Input, Modal, Select, Switch, api, toast } from '@connect/web-shared';

export interface OrgUnit {
  public_id: string;
  name: string;
  code?: string | null;
  is_active?: boolean;
  parent_name?: string | null;
  department_name?: string | null;
}

const schema = z.object({
  name: z.string().trim().min(1, 'It needs a name.').max(160),
  code: z.string().trim().max(32).optional(),
  parent_id: z.string().optional(),
  is_active: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const DEFAULTS: FormValues = { name: '', code: '', parent_id: '', is_active: true };

export function OrgUnitDialog({
  open,
  onClose,
  onSaved,
  kind,
  unit,
  departments,
}: {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  kind: 'department' | 'team';
  /** The unit being edited, or `null` to create one. */
  unit: OrgUnit | null;
  /** Every department, for the parent picker. */
  departments: { public_id: string; name: string }[];
}) {
  const editing = unit !== null;
  const isDepartment = kind === 'department';
  const path = isDepartment ? 'departments' : 'teams';

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  useEffect(() => {
    if (!open) return;
    form.reset(
      unit
        ? {
            name: unit.name,
            code: unit.code ?? '',
            parent_id: '',
            is_active: unit.is_active ?? true,
          }
        : DEFAULTS,
    );
  }, [open, unit, form]);

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const payload: Record<string, unknown> = {
        name: values.name,
        code: values.code?.trim() || null,
        is_active: values.is_active,
      };
      // Named differently on each, because they mean different things: a
      // department's parent is another department; a team's is the department
      // it belongs to, and it is required.
      if (isDepartment) {
        payload.parent_id = values.parent_id || null;
      } else {
        payload.department_id = values.parent_id || null;
      }
      if (editing) return api.patch(`/hr/${path}/${unit.public_id}`, payload);
      return api.post(`/hr/${path}`, payload);
    },
    onSuccess: () => {
      toast.success(editing ? 'Saved' : `${isDepartment ? 'Department' : 'Team'} created`);
      onSaved?.();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (field in DEFAULTS) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        if (!placed) form.setError('root', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  const submit = () => {
    if (!isDepartment && !editing && !form.getValues('parent_id')) {
      form.setError('parent_id', { message: 'A team sits inside a department.' });
      return;
    }
    void form.handleSubmit((values) => save.mutateAsync(values))();
  };

  const parentOptions = [
    { value: '', label: isDepartment ? 'Top level' : 'Choose a department' },
    ...departments
      // A department cannot be its own parent. The deeper cycle check is the
      // server's; this removes the one case the form can see.
      .filter((d) => d.public_id !== unit?.public_id)
      .map((d) => ({ value: d.public_id, label: d.name })),
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        editing
          ? `Edit ${isDepartment ? 'department' : 'team'}`
          : `New ${isDepartment ? 'department' : 'team'}`
      }
      size="sm"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button size="sm" loading={save.isPending} onClick={submit}>
            {editing ? 'Save changes' : 'Create'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {form.formState.errors.root?.message ? (
          <Alert tone="danger">{form.formState.errors.root.message}</Alert>
        ) : null}

        <Input
          label="Name"
          required
          error={form.formState.errors.name?.message}
          {...form.register('name')}
        />
        <Input
          label="Code"
          hint="Optional. A short label for reports and exports."
          error={form.formState.errors.code?.message}
          {...form.register('code')}
        />
        <Select
          label={isDepartment ? 'Inside' : 'Department'}
          required={!isDepartment}
          options={parentOptions}
          hint={
            isDepartment
              ? 'A department inside another describes a division.'
              : 'A team-scoped role reaches only this team.'
          }
          error={form.formState.errors.parent_id?.message}
          {...form.register('parent_id')}
        />

        {editing ? (
          <Switch
            label="Active"
            hint="An inactive unit stays for history and stops being offered on new records."
            {...form.register('is_active')}
          />
        ) : null}
      </form>
    </Modal>
  );
}
